import { ProductTypes, ProductType } from '../types';
import { getRegionByProduct } from '../i18n/utils';
import { deriveCurrencyFormat } from '../i18n/currencies';
import ProductStatics from '../console/_statics/product.statics.json';
import {
  languages,
  COUNTRY_MAPPING,
  type Languages,
} from '../i18n/i18n.language-config';
import { getPlatformRegion } from './app.utils';

export const stringsToBools = (
  object: Record<string, unknown>,
  TRUE_STRING = 'yes',
  FALSE_STRING = 'no'
) => {
  return Object.keys(object).reduce((a, key) => {
    let value = object[key];

    if (value === TRUE_STRING) {
      value = true;
    }

    if (value === FALSE_STRING) {
      value = false;
    }

    return { ...a, [key]: value };
  }, {});
};

export const _isOneOf = (value: unknown, options: Array<unknown>) => {
  for (const element of options) {
    if (value === element) {
      return true;
    }
  }

  return false;
};

/**
 * Pretty Id
 * @param charProfile - 36 encompasses full alpha numeric character set (e.g. 10 would only be numbers)
 * @param prefix
 * @param suffix
 */
export const prettyId = (charProfile = 36, prefix = '', suffix = '') =>
  `${prefix}${Math.random().toString(charProfile).slice(2)}${suffix}`;

export const prepColumnFilters = (columns: any) => {
  return columns.reduce(
    (acc: any, column: any) =>
      typeof column.filterType === 'string'
        ? {
            ...acc,
            [column.name]: {
              text: column.label || column.title,
              type: column.filterType,
              getListOptions: column.getListOptions,
              renderListOption: column.renderListOption,
            },
          }
        : acc,
    {}
  );
};

/**
 * Raw (unformatted) total fee for a policy/quote/binder row — broker fee (+
 * additional broker fee) for P100, MGA fee for everything else. Split out of
 * `getFee` so JSX call sites can render it with `<CurrencyAmount>` using the
 * row's own `currency` instead of `getFee`'s pre-formatted, region-derived string.
 */
export const getFeeAmount = (row: any): number => {
  const additionalBrokerFee = row.additionalBrokerFee || 0;
  const mgaFee = row.mgaFee || 0;
  const brokerFee = row.brokerFee || 0;

  if (row.product === ProductTypes.p100) {
    return brokerFee + additionalBrokerFee;
  }
  return mgaFee;
};

export const getFee = (row: any) => {
  const totalFee = getFeeAmount(row);
  const language = getRegionByProduct(row.product);
  return totalFee ? deriveCurrencyFormat(language, totalFee) : '-';
};

export const getProductsByRegion = (region: Languages) => {
  return ProductStatics.PRODUCT_OPTIONS.filter((product: any) =>
    product.regions.includes(region)
  );
};

export const getDeepNestedValueByKey = (
  searchedObject: unknown,
  searchedKey: string
): Record<string, unknown> | undefined => {
  let respectiveValue;
  const hasKeyField = (obj: unknown, key: string): unknown => {
    if (!obj || typeof obj !== 'object') {
      return false; // If obj is null, undefined, or not an object, return false
    }

    if (key in obj) {
      respectiveValue = (obj as Record<string, unknown>)?.[key];
      return true; // If key field is present in obj, return true
    }

    return Object.values(obj).some((val: any) => hasKeyField(val, key));
  };

  hasKeyField(searchedObject, searchedKey);

  return respectiveValue;
};

export const doesProductUseSupplementalFirmo = (
  productType?: ProductType
): boolean => {
  return (
    productType === ProductTypes.prime_tech ||
    productType === ProductTypes.prime_one_tech
  );
};

export const usesSicClassification = (submissionCountry?: string): boolean => {
  const language = submissionCountry
    ? COUNTRY_MAPPING[submissionCountry as keyof typeof COUNTRY_MAPPING]
    : languages[getPlatformRegion()];

  return language === languages['en-GB'] || language === languages['en-AU'];
};

/** Mirrors backend StateUtils.getStateDefaultCountry: use state when present, else fall back to country. */
export const getStateDefaultCountry = (
  state?: string | null,
  country?: string | null
): string | undefined => (state ? state : country ?? undefined);
